import { create } from 'zustand'

export type NestedTracksTab = 'mixes' | 'similar-tracks'

type NestedTracksHistoryEntry = {
	source: string
	tab: NestedTracksTab
	track: Track
}

type NavigateForTrackOptions = {
	resetStack?: boolean
	tab: NestedTracksTab
}

type NestedTracksState = {
	history: NestedTracksHistoryEntry[]
	/** Changes when the tray moves to an entry the tab list did not pick itself. */
	navigationId: number
	source: string | null
	tab: NestedTracksTab | null
	track: Track | null
	changeTab: (tab: NestedTracksTab) => void
	clear: () => void
	goBack: () => void
	navigateForTrack: (
		track: Track,
		source: string,
		options: NavigateForTrackOptions
	) => void
}

const useNestedTracksStore = create<NestedTracksState>()((set, get) => ({
	history: [],
	navigationId: 0,
	track: null,
	source: null,
	tab: null,
	navigateForTrack: (track, source, { resetStack, tab }) => {
		const { track: currentTrack, tab: currentTab } = get()
		if (currentTrack?.id === track.id && currentTab === tab) {
			return
		}

		const entry = { track, source, tab }

		set((state) => ({
			...entry,
			history: resetStack ? [entry] : [...state.history, entry],
			navigationId: state.navigationId + 1,
		}))
	},
	changeTab: (tab) => {
		const { track, source, tab: currentTab } = get()
		if (!track || source === null || currentTab === tab) {
			return
		}

		const entry = { track, source, tab }

		set((state) => ({ ...entry, history: [...state.history, entry] }))
	},
	goBack: () => {
		const { history } = get()
		if (history.length <= 1) {
			return
		}

		const nextHistory = history.slice(0, -1)
		const previous = nextHistory[nextHistory.length - 1]

		set((state) => ({
			...previous,
			history: nextHistory,
			navigationId: state.navigationId + 1,
		}))
	},
	clear: () => {
		set({ history: [], track: null, source: null, tab: null })
	},
}))

export default useNestedTracksStore
